// Hooks for service status dashboard — health monitoring + incident management

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  ActiveIncidentsResponse,
  RecentIncidentsResponse,
  SystemStatus,
  UptimeResponse,
} from '@/lib/types-status'

const STATUS_REFETCH_MS = 30_000

export function useSystemStatus() {
  return useQuery<SystemStatus>({
    queryKey: ['status', 'overview'],
    queryFn: () => apiClient.get(endpoints.status.overview),
    refetchInterval: STATUS_REFETCH_MS,
  })
}

export function useStatusUptime(serviceId: string) {
  return useQuery<UptimeResponse>({
    queryKey: ['status', 'uptime', serviceId],
    queryFn: () => apiClient.get(endpoints.status.uptime(serviceId)),
    enabled: !!serviceId,
  })
}

export function useActiveIncidents() {
  return useQuery<ActiveIncidentsResponse>({
    queryKey: ['status', 'incidents', 'active'],
    queryFn: () => apiClient.get(endpoints.status.incidentsActive),
    refetchInterval: STATUS_REFETCH_MS,
  })
}

export function useRecentIncidents() {
  return useQuery<RecentIncidentsResponse>({
    queryKey: ['status', 'incidents', 'recent'],
    queryFn: () => apiClient.get(endpoints.status.incidentsRecent),
  })
}

export function useCreateIncident() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      title: string
      severity: string
      affectedServices: string[]
      message: string
    }) => apiClient.post(endpoints.status.createIncident, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['status', 'incidents'] })
    },
  })
}

export function useUpdateIncident() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      apiClient.patch(endpoints.status.updateIncident(String(id)), data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['status', 'incidents'] })
    },
  })
}

export function useAddIncidentUpdate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { status: string; message: string } }) =>
      apiClient.post(endpoints.status.addUpdate(String(id)), data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['status', 'incidents'] })
    },
  })
}

export function useDeleteIncident() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiClient.del(endpoints.status.deleteIncident(String(id))),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['status', 'incidents'] })
    },
  })
}

export function useSetMaintenance() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { serviceId: string; enabled: boolean; message?: string }) =>
      apiClient.post(endpoints.status.maintenance, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['status'] })
    },
  })
}
